import React, { useMemo, useReducer, useEffect, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import DataRequests from "utils/data";
import { getCookie } from "../utils/cookiesHelper";
import stompChatService from "../../../../services/StompChatService";
import ChatServices from "../services/ChatServices";

import ChatContext from "./ChatContext";
import chatReducer, { CHAT_INITIAL_STATE } from "../reducers/chatReducer";
import CHAT_ACTIONS_TYPES from "../reducers/actionTypes/chatActionTypes";

// Lazy-load notification audio to avoid loading on login page
let notificationAudio = null;
const playNotificationSound = () => {
  try {
    if (!notificationAudio) {
      // eslint-disable-next-line global-require
      const audioSrc = require("../../../../assets/notification.wav");
      notificationAudio = new Audio(audioSrc);
    }
    notificationAudio.currentTime = 0;
    notificationAudio.play().catch((err) => {
      console.warn("Failed to play notification sound:", err);
    });
  } catch (err) {
    console.warn("Failed to load notification audio:", err);
  }
};

const ChatContextProvider = ({ children }) => {
  const [state, dispatch] = useReducer(chatReducer, CHAT_INITIAL_STATE);
  const orgUserToken = getCookie("X-User-Token");
  // Get orgId and userId from Redux store
  const orgId = useSelector((reduxState) =>
    reduxState.getIn(["users", "user", "info", "organization_id"])
  );
  const userId = useSelector((reduxState) =>
    reduxState.getIn(["users", "user", "info", "id"])
  );

  // Socket connection effect
  useEffect(() => {
    if (orgUserToken && userId) {
      let typingClearTimer = null;
      const onConnected = () => {
        dispatch({
          type: "SOCKET_CONNECTED",
          payload: { connected: true }
        });

        stompChatService.unsubscribeTyping();
        stompChatService.subscribeTyping((body) => {
          const msg = typeof body === "string" ? body : String(body);
          const isTypingMsg = msg.includes("is typing in chat");
          const isStopMsg = msg.includes("stopped typing in chat");
          if (!isTypingMsg && !isStopMsg) return;
          const chatIdMatch = msg.match(/in chat (.+)$/);
          const typingChatId = chatIdMatch ? chatIdMatch[1] : null;
          const senderMatch = msg.match(/^(\S+)/);
          const typingSenderId = senderMatch ? senderMatch[1] : null;
          if (String(typingSenderId) === String(userId)) return;
          clearTimeout(typingClearTimer);
          if (isTypingMsg) {
            dispatch({ type: CHAT_ACTIONS_TYPES.SET_TYPING, payload: { isTyping: true, roomId: typingChatId } });
            typingClearTimer = setTimeout(() => { dispatch({ type: CHAT_ACTIONS_TYPES.SET_TYPING, payload: { isTyping: false, roomId: typingChatId } }); }, 15000);
          } else {
            typingClearTimer = setTimeout(() => { dispatch({ type: CHAT_ACTIONS_TYPES.SET_TYPING, payload: { isTyping: false, roomId: typingChatId } }); }, 2000);
          }
        });
      };

      const onMessage = (payload) => {
        // Skip streaming intermediate messages — only show final STREAM_END
        const opType = payload.messageOperationType;
        if (opType === "STREAM_START" || opType === "stream_start") return;
        if (opType === "STREAM_CHUNK" || opType === "stream_chunk") return;

        // Skip bot error messages
        const isErrorMessage = payload.content?.toLowerCase().includes("sorry, i encountered an error");
        if (isErrorMessage) return;

        // Play notification sound for incoming customer messages
        const isFromCustomer = payload.senderType === "USER" || (payload.senderId && String(payload.senderId) !== String(userId));
        const isNewMessage = payload.type === "new_message" || (payload.chatId && payload.content && !payload.type);
        if (isFromCustomer && isNewMessage) {
          clearTimeout(typingClearTimer);
          dispatch({ type: CHAT_ACTIONS_TYPES.SET_TYPING, payload: { isTyping: false, roomId: String(payload.chatId) } });
          playNotificationSound();
          if (document.hidden) {
            document.title = "New Message...";
          }
        }

        // Handle different message types (only non-bot messages)
        if (payload.type === "new_message") {
          dispatch({
            type: "ADD_NEW_MESSAGE_TO_ROOM",
            payload: { newMessage: payload, currentChatId: payload.chatId },
          });

          // Update rooms latestMessage
          dispatch({
            type: "UPDATE_LATEST_MESSAGE_AND_SORT",
            payload: {
              roomUpdated: {
                _id: payload.chatId || payload._id,
                latestMessage: payload,
                updatedAt: payload.createdAt || new Date().toISOString()
              }
            }
          });
        } else if (payload.type === "update_chat_latest_message") {
          dispatch({
            type: "UPDATE_LATEST_MESSAGE_AND_SORT",
            payload: { roomUpdated: payload },
          });
        } else if (payload.type === "chat_room_updated") {
          dispatch({
            type: "UPDATE_ROOM",
            payload: { roomUpdated: payload },
          });
        } else if (payload.chatId && payload.content) {
          // Regular message - add to room and update latest message

          dispatch({
            type: "ADD_NEW_MESSAGE_TO_ROOM",
            payload: { newMessage: payload, currentChatId: payload.chatId },
          });

          // Also update the latest message in the room
          dispatch({
            type: "UPDATE_LATEST_MESSAGE_AND_SORT",
            payload: {
              roomUpdated: {
                _id: payload.chatId,
                latestMessage: payload,
                updatedAt: payload.createdAt || new Date().toISOString()
              }
            },
          });
        }
      };

      const onError = (error) => {
        console.error("STOMP WebSocket connection error:", error);
      };

      stompChatService.connect(orgUserToken, onConnected, onMessage, onError, orgId, userId);

      // Listen for handoff notifications (customer requested human agent)
      stompChatService.onChatUpdates((notification) => {
        if (notification.type === "HANDOFF_REQUESTED") {
          dispatch({
            type: CHAT_ACTIONS_TYPES.ADD_HANDOFF_CHAT,
            payload: { chatId: notification.chatId },
          });

          // Sound alert
          playNotificationSound();

          // Browser notification (if permitted)
          if (Notification.permission === "granted") {
            const n = new Notification("Customer needs help!", {
              body: `Chat #${notification.chatId} - Customer requested a human agent`,
              icon: "/favicon.ico",
              requireInteraction: true,
            });
            n.onclick = () => {
              window.focus();
              window.location.hash = `/chat/${notification.chatId}/chat`;
              n.close();
            };
          } else if (Notification.permission !== "denied") {
            Notification.requestPermission();
          }

          // Tab title flash
          const originalTitle = document.title;
          const flashInterval = setInterval(() => {
            document.title = document.title === originalTitle
              ? "*** CUSTOMER NEEDS HELP ***"
              : originalTitle;
          }, 1000);
          const stopFlash = () => {
            clearInterval(flashInterval);
            document.title = originalTitle;
            document.removeEventListener("visibilitychange", stopFlash);
          };
          document.addEventListener("visibilitychange", stopFlash);
          setTimeout(stopFlash, 30000);

          // Persistent toast
          toast.warn(
            `Customer needs help! Chat #${notification.chatId}`,
            { autoClose: false }
          );
        }
      });
    }

    return () => {
      stompChatService.onChatUpdates(null);
      stompChatService.unsubscribeTyping();
      stompChatService.disconnect();
    };
  }, [orgUserToken, orgId, userId]);

  // ---- Direct chat pagination helper ----

  const fetchChatHistory = useCallback(async ({ chatId, page = 0, mode = "replace" }) => {
    dispatch({ type: CHAT_ACTIONS_TYPES.SET_LOADING_CHAT, payload: { loadingChat: true } });
    try {
      const result = await ChatServices.getChatHistory({ jwtToken: orgUserToken, chatId, page });
      if (mode === "appendOlder") {
        dispatch({ type: CHAT_ACTIONS_TYPES.APPEND_OLDER_MESSAGES, payload: { messages: result.content } });
      } else {
        // Reset pagination and set initial messages in one logical operation
        dispatch({ type: CHAT_ACTIONS_TYPES.SET_CHAT_MESSAGES, payload: { messages: result.content, chatResponse: {} } });
      }
      dispatch({ type: CHAT_ACTIONS_TYPES.SET_CHAT_HAS_MORE, payload: { chatHasMore: !result.last } });
      dispatch({ type: CHAT_ACTIONS_TYPES.SET_CHAT_PAGE, payload: { chatPage: page } });
    } catch (e) {
      console.error("Failed to load chat history:", e);
      toast.error("Failed to load chat history");
    } finally {
      dispatch({ type: CHAT_ACTIONS_TYPES.SET_LOADING_CHAT, payload: { loadingChat: false } });
    }
  }, [orgUserToken]);

  // ---- Group chat helpers ----

  const selectedGroupRoomRef = useRef(null);

  const fetchGroupRooms = useCallback(async () => {
    dispatch({ type: CHAT_ACTIONS_TYPES.SET_LOADING_GROUP_ROOMS, payload: { loadingGroupRooms: true } });
    try {
      const rooms = await ChatServices.getGroupRooms({ jwtToken: orgUserToken });
      dispatch({ type: CHAT_ACTIONS_TYPES.SET_GROUP_ROOMS, payload: { groupRooms: rooms } });
    } catch (e) {
      console.error("Failed to load group rooms:", e);
    } finally {
      dispatch({ type: CHAT_ACTIONS_TYPES.SET_LOADING_GROUP_ROOMS, payload: { loadingGroupRooms: false } });
    }
  }, [orgUserToken]);

  const fetchGroupMessages = useCallback(async ({ groupId, page = 0, mode = "replace" }) => {
    dispatch({ type: CHAT_ACTIONS_TYPES.SET_LOADING_GROUP_CHAT, payload: { loadingGroupChat: true } });
    try {
      const result = await ChatServices.getGroupMessages({ jwtToken: orgUserToken, groupId, page });
      const actionType = mode === "appendOlder"
        ? CHAT_ACTIONS_TYPES.APPEND_OLDER_GROUP_MESSAGES
        : CHAT_ACTIONS_TYPES.SET_GROUP_MESSAGES;
      dispatch({ type: actionType, payload: { groupMessages: result.content } });
      dispatch({ type: CHAT_ACTIONS_TYPES.SET_GROUP_HAS_MORE, payload: { groupHasMore: !result.last } });
      dispatch({ type: CHAT_ACTIONS_TYPES.SET_GROUP_PAGE, payload: { groupPage: page } });
    } catch (e) {
      console.error("Failed to load group messages:", e);
    } finally {
      dispatch({ type: CHAT_ACTIONS_TYPES.SET_LOADING_GROUP_CHAT, payload: { loadingGroupChat: false } });
    }
  }, [orgUserToken]);

  const checkGroupMembership = useCallback(async (groupId) => {
    try {
      const details = await ChatServices.getGroupDetails({ jwtToken: orgUserToken, groupId });
      const members = details?.users || [];
      const joined = members.some(
        (m) => m.chatUser?.id === userId || m.externalUserId === userId || m.chatUser?.id === orgId
      );
      dispatch({ type: CHAT_ACTIONS_TYPES.SET_GROUP_JOINED, payload: { isGroupJoined: joined } });
      if (joined) {
        dispatch({ type: CHAT_ACTIONS_TYPES.SET_GROUP_MEMBERS, payload: { groupMembers: members } });
        await fetchGroupMessages({ groupId, page: 0, mode: "replace" });
      }
    } catch (e) {
      console.error("Failed to check group membership:", e);
    }
  }, [orgUserToken, userId, orgId, fetchGroupMessages]);

  const selectGroupRoom = useCallback(async (room) => {
    selectedGroupRoomRef.current = room;
    dispatch({ type: CHAT_ACTIONS_TYPES.SET_SELECTED_GROUP_ROOM, payload: { selectedGroupRoom: room } });
    await checkGroupMembership(room.id || room._id);
  }, [checkGroupMembership]);

  const joinGroup = useCallback(async (groupId) => {
    try {
      await ChatServices.joinGroup({ jwtToken: orgUserToken, groupId });
      dispatch({ type: CHAT_ACTIONS_TYPES.SET_GROUP_JOINED, payload: { isGroupJoined: true } });
      await fetchGroupMessages({ groupId, page: 0, mode: "replace" });
    } catch (e) {
      // 400 = already member — treat as joined
      if (e?.response?.status === 400) {
        dispatch({ type: CHAT_ACTIONS_TYPES.SET_GROUP_JOINED, payload: { isGroupJoined: true } });
        await fetchGroupMessages({ groupId, page: 0, mode: "replace" });
      } else {
        console.error("Failed to join group:", e);
      }
    }
  }, [orgUserToken, fetchGroupMessages]);

  const createGroupRoom = useCallback(async (formData) => {
    const room = await DataRequests.createGroupChannel(formData);
    dispatch({ type: CHAT_ACTIONS_TYPES.PREPEND_GROUP_ROOM, payload: { room } });
    toast.success("Group channel created successfully");
    return room;
  }, []);

  const deleteGroupRoom = useCallback(async (groupId) => {
    await DataRequests.deleteGroupChannel(groupId);
    dispatch({ type: CHAT_ACTIONS_TYPES.REMOVE_GROUP_ROOM, payload: { groupId } });
    toast.success("Group chat deleted successfully");
  }, []);

  // ---- Support agents (duty / queue / claim / release) ----

  const setDuty = useCallback(async (onDuty) => {
    try {
      const duty = await ChatServices.setDuty({ userToken: orgUserToken, onDuty });
      dispatch({ type: CHAT_ACTIONS_TYPES.SET_SUPPORT_DUTY, payload: { dutyOn: !!duty?.onDuty } });
      return duty;
    } catch (e) {
      toast.error("Failed to update duty state");
      throw e;
    }
  }, [orgUserToken]);

  const fetchSupportQueue = useCallback(async (filter) => {
    dispatch({ type: CHAT_ACTIONS_TYPES.SET_LOADING_SUPPORT_QUEUE, payload: { loadingSupportQueue: true } });
    try {
      const queue = await ChatServices.getSupportQueue({ userToken: orgUserToken, filter });
      dispatch({ type: CHAT_ACTIONS_TYPES.SET_SUPPORT_QUEUE, payload: { supportQueue: queue, filter } });
    } catch (e) {
      console.error("Failed to load support queue:", e);
      toast.error("Failed to load support queue");
    } finally {
      dispatch({ type: CHAT_ACTIONS_TYPES.SET_LOADING_SUPPORT_QUEUE, payload: { loadingSupportQueue: false } });
    }
  }, [orgUserToken]);

  const claimConversation = useCallback(async (chatId) => {
    try {
      const result = await ChatServices.claimConversation({ userToken: orgUserToken, chatId });
      if (result?.claimed) {
        dispatch({ type: CHAT_ACTIONS_TYPES.CLAIMED_CONVERSATION, payload: { chatId } });
        toast.success("Conversation taken");
      }
      return result;
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.info(`Already taken by ${e?.response?.data?.holderName || "another agent"}`);
      } else {
        toast.error("Could not take conversation");
      }
      throw e;
    }
  }, [orgUserToken]);

  const releaseConversation = useCallback(async (chatId) => {
    try {
      const result = await ChatServices.releaseConversation({ userToken: orgUserToken, chatId });
      if (result?.released) {
        dispatch({ type: CHAT_ACTIONS_TYPES.RELEASED_CONVERSATION, payload: { chatId } });
        toast.success("Conversation handed back");
      }
      return result;
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.warn("You are not holding this conversation");
      } else {
        toast.error("Could not hand back conversation");
      }
      throw e;
    }
  }, [orgUserToken]);

  const fetchSupportAgents = useCallback(async () => {
    dispatch({ type: CHAT_ACTIONS_TYPES.SET_LOADING_SUPPORT_AGENTS, payload: { loadingSupportAgents: true } });
    try {
      const agents = await ChatServices.getSupportAgents({ userToken: orgUserToken });
      dispatch({ type: CHAT_ACTIONS_TYPES.SET_SUPPORT_AGENTS, payload: { supportAgents: agents } });
    } catch (e) {
      console.error("Failed to load support agents:", e);
      toast.error("Failed to load support team");
    } finally {
      dispatch({ type: CHAT_ACTIONS_TYPES.SET_LOADING_SUPPORT_AGENTS, payload: { loadingSupportAgents: false } });
    }
  }, [orgUserToken]);

  const setAgentDuty = useCallback(async (agentId, onDuty) => {
    try {
      await ChatServices.setAgentDuty({ userToken: orgUserToken, agentId, onDuty });
      dispatch({
        type: CHAT_ACTIONS_TYPES.PATCH_SUPPORT_AGENT,
        payload: { agentId, patch: { onDuty: !!onDuty, onDutySince: onDuty ? new Date().toISOString() : null } },
      });
      toast.success(onDuty ? "Agent set on duty" : "Agent set off duty");
      return true;
    } catch (e) {
      const message = e?.response?.data?.message || (onDuty ? "Could not set agent on duty" : "Could not set agent off duty");
      toast.error(message);
      throw e;
    }
  }, [orgUserToken]);

  const sendGroupMessage = useCallback((content) => {
    const room = selectedGroupRoomRef.current;
    if (!room || !content?.trim()) return;
    const groupId = room.id || room._id;
    const tempMessage = {
      _id: `temp_${Date.now()}`,
      _isMine: true,
      content,
      groupId,
      senderId: userId,
      senderName: "You",
      sender: { id: userId, externalUserId: userId, name: "You" },
      createdAt: new Date().toISOString(),
    };
    dispatch({ type: CHAT_ACTIONS_TYPES.ADD_NEW_GROUP_MESSAGE, payload: { newMessage: tempMessage } });
    stompChatService.sendGroupMessage(orgUserToken, { content, groupId, senderId: userId });
  }, [orgUserToken, userId]);

  // Group WebSocket subscription — re-subscribe whenever selectedGroupRoom changes
  const { selectedGroupRoom } = state;
  useEffect(() => {
    if (!selectedGroupRoom || !userId) return;
    const groupId = selectedGroupRoom.id || selectedGroupRoom._id;

    const handleGroupMessage = (payload) => {
      dispatch({ type: CHAT_ACTIONS_TYPES.ADD_NEW_GROUP_MESSAGE, payload: { newMessage: payload } });
    };

    stompChatService.subscribeGroupMessages(handleGroupMessage, userId, groupId);

    return () => {
      stompChatService.unsubscribeGroupMessages();
    };
  }, [selectedGroupRoom, userId]);

  const providerState = useMemo(
    () => ({
      state,
      dispatch,
      // Direct chat pagination
      fetchChatHistory,
      // Group helpers exposed via context
      fetchGroupRooms,
      selectGroupRoom,
      joinGroup,
      sendGroupMessage,
      fetchGroupMessages,
      createGroupRoom,
      deleteGroupRoom,
      // Support agents (duty / queue / claim / release)
      setDuty,
      fetchSupportQueue,
      claimConversation,
      releaseConversation,
      // Support roster + owner override
      fetchSupportAgents,
      setAgentDuty,
    }),
    [state, fetchChatHistory, fetchGroupRooms, selectGroupRoom, joinGroup, sendGroupMessage, fetchGroupMessages, createGroupRoom, deleteGroupRoom, setDuty, fetchSupportQueue, claimConversation, releaseConversation, fetchSupportAgents, setAgentDuty]
  );

  return (
    <ChatContext.Provider value={providerState}>
      {children}
    </ChatContext.Provider>
  );
};

export default ChatContextProvider;
